import { useCallback, useEffect, useMemo, useState } from "react";
import type { TimelineTransition, TimelineAction } from "@scroll/shared-types/timeline";
import { MIN_TRANSITION_DURATION, MAX_TRANSITION_DURATION } from "@scroll/shared-types/timeline";
import {
  BETWEEN_CLIPS_TRANSITION_DEFINITIONS,
  getTransitionDefinition,
} from "@scroll/shared-types/transitions";
import { generateId } from "@scroll/shared-types/timeline-utils";

interface TransitionPickerProps {
  transition: TimelineTransition;
  transitionIndex: number;
  onDispatch: (action: TimelineAction) => void;
  onClose: () => void;
}

export default function TransitionPicker({
  transition,
  transitionIndex,
  onDispatch,
  onClose,
}: TransitionPickerProps) {
  // This picker only ever edits transitions[i] (between-clips boundaries) —
  // the web editor has no intro/outro bookend UI yet — so the intro-/outro-
  // only fade flavors aren't offered. But legacy drafts saved while the
  // unfiltered picker shipped can carry a bookend type in a between slot:
  // append that type's definition so the current selection stays visible
  // and re-selectable instead of silently showing nothing active.
  const definitions = useMemo(() => {
    if (BETWEEN_CLIPS_TRANSITION_DEFINITIONS.some((def) => def.type === transition.type)) {
      return BETWEEN_CLIPS_TRANSITION_DEFINITIONS;
    }
    return [...BETWEEN_CLIPS_TRANSITION_DEFINITIONS, getTransitionDefinition(transition.type)];
  }, [transition.type]);

  const handleSelect = useCallback(
    (type: TimelineTransition["type"]) => {
      const duration = type === "cut" ? 0 : (transition.duration || 0.5);
      onDispatch({
        type: "SET_TRANSITION",
        index: transitionIndex,
        transition: { id: transition.id || generateId(), type, duration },
      });
      if (type === "cut") onClose();
    },
    [transition, transitionIndex, onDispatch, onClose],
  );

  // Track slider value locally to avoid creating undo entries on every tick
  const [localDuration, setLocalDuration] = useState(transition.duration);

  // Re-sync when the stored duration changes from outside the slider (e.g.
  // selecting a different transition type resets it) — otherwise the slider
  // shows, and would re-commit, the stale value.
  useEffect(() => {
    setLocalDuration(transition.duration);
  }, [transition.duration]);

  const handleDurationInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalDuration(parseFloat(e.target.value));
    },
    [],
  );

  const handleDurationCommit = useCallback(() => {
    onDispatch({
      type: "SET_TRANSITION",
      index: transitionIndex,
      transition: { ...transition, duration: localDuration },
    });
  }, [transition, transitionIndex, onDispatch, localDuration]);

  return (
    <div
      className="transition-picker-container"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-labelledby="transition-picker-title"
    >
      <div className="transition-picker-header">
        <span className="transition-picker-title" id="transition-picker-title">Transition</span>
        <button
          className="transition-picker-close"
          onClick={onClose}
          aria-label="Close transition picker"
        >
          &times;
        </button>
      </div>

      <div className="transition-picker-grid" role="radiogroup" aria-label="Transition type">
        {definitions.map((def) => (
          <button
            key={def.type}
            className={`transition-picker-item${transition.type === def.type ? " active" : ""}`}
            onClick={() => handleSelect(def.type)}
            title={def.description}
            role="radio"
            aria-checked={transition.type === def.type}
            aria-label={`${def.label} — ${def.description}`}
          >
            <span className="transition-picker-item-icon" aria-hidden="true">{def.icon}</span>
            <span className="transition-picker-item-label">{def.label}</span>
          </button>
        ))}
      </div>

      {transition.type !== "cut" && (
        <div className="transition-picker-duration">
          <label className="transition-picker-duration-label">
            Duration: {localDuration.toFixed(1)}s
          </label>
          <input
            type="range"
            min={MIN_TRANSITION_DURATION}
            max={MAX_TRANSITION_DURATION}
            step={0.1}
            value={localDuration}
            onChange={handleDurationInput}
            onMouseUp={handleDurationCommit}
            onTouchEnd={handleDurationCommit}
            className="clip-inspector-slider"
          />
          <div className="clip-inspector-slider-labels">
            <span>{MIN_TRANSITION_DURATION}s</span>
            <span>{MAX_TRANSITION_DURATION}s</span>
          </div>
        </div>
      )}
    </div>
  );
}
